import React from 'react';
import Button from './Button';

interface HomePageProps {
  onGetStarted: () => void;
  onContact?: () => void;
  isLoggedIn?: boolean;
}

const featureHighlights = [
  {
    title: 'Fine-tuned for the HSC classroom',
    description:
      'Purpose-built ensembles calibrated with NSW HSC history exemplars, nuanced rubrics, and Australian-English tone so the output sounds like it came from your faculty office.',
  },
  {
    title: 'Calibrated marking, essay by essay',
    description: 'Each essay is marked by a specialised model calibrated specifically for NSW HSC history essays.',
  },
  {
    title: 'Full rubric intelligence',
    description: 'Criteria weighting, command terms, contextual knowledge, and source integration are all scored individually before combining into an overall grade.',
  },
  {
    title: 'Feedback teachers can trust',
    description: 'Evidence-backed commentary, actionable next steps, and summary reports ready for parent communication or faculty moderation.',
  },
];

const processPillars = [
  {
    step: '1. Capture & clean the submission',
    detail: 'Images, PDFs, and DOCX files are transcribed, stripped of teacher notes, and structured so every criterion has a clean text stream.',
  },
  {
    step: '2. Augment with context',
    detail: 'The assessment brief, rubric, year level, and syllabus focus are injected so every model understands what "good" looks like in an Australian classroom.',
  },
  {
    step: '3. Run calibrated models',
    detail: 'A fine-tuned GPT marks each essay with tight temperature controls and HSC-specific prompts.',
  },
  {
    step: '4. Moderate & justify',
    detail: 'Marks are consolidated with confidence scoring and qualitative justifications so you can see exactly how each conclusion was reached.',
  },
];

const differentiators = [
  {
    llm: 'Limitations of trying to use LLM to mark essays and provide useful feedback',
    markerMate: 'AI Marker Mate',
    items: [
      {
        llm: 'Single, generic prompt produces one-off marks with no guardrails.',
        markerMate: 'Dedicated rubric parser that weights each criterion before marking begins.',
      },
      {
        llm: 'No NSW syllabus context, so nuance and command terms are missed.',
        markerMate: 'Models pre-loaded with NSW syllabus cues, command terms, and exemplar phrasing.',
      },
      {
        llm: 'No requirement to cite evidence, so bands can be hallucinated.',
        markerMate: 'Requires quoted evidence and rationale for every band before a mark is accepted.',
      },
      {
        llm: 'No calibration, so temperature swings drive inconsistent marks.',
        markerMate: 'Ensemble consensus with variance detection, moderation notes, and audit logs.',
      },
    ],
  },
];

const HomePage: React.FC<HomePageProps> = ({ onGetStarted, onContact, isLoggedIn = false }) => {
  const vennImage =
    'https://firebasestorage.googleapis.com/v0/b/history-essay-bot.firebasestorage.app/o/venn%20diagram%20of%20results%20from%20LLMs.png?alt=media&token=e48f5dd2-6d0f-4380-bef6-1d2cfc5e7928';

  const handleContactClick = () => {
    if (onContact) {
      onContact();
    } else {
      onGetStarted();
    }
  };

  return (
    <div className="bg-background text-foreground">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#1a1040] via-primary to-[#2d1a5e] text-primary-foreground">
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle,_rgba(255,255,255,0.2)_1px,_transparent_1px)] bg-[length:44px_44px]" />
        <div className="relative max-w-6xl mx-auto px-6 py-16 sm:py-20 lg:py-24">
          <div className="max-w-3xl">
            <p className="inline-flex items-center text-xs font-semibold tracking-widest uppercase bg-white/10 px-3 py-1 rounded-full mb-6">
              Built with senior Australian history markers
            </p>
            <h1 className="text-4xl sm:text-5xl font-semibold leading-tight mb-6">
              HSC-ready essay marking that goes far beyond a generic LLM prompt.
            </h1>
            <p className="text-lg sm:text-xl text-primary-foreground/90 leading-relaxed mb-8">
              AI Marker Mate layers calibrated, fine-tuned models, rubric intelligence, and moderation workflows on top of GPT, Claude,
              and Gemini so every essay is scored the way an experienced teacher would, complete with evidence-rich feedback.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button onClick={onGetStarted} size="lg" className="w-full sm:w-auto bg-white text-primary hover:bg-white/90">
                {isLoggedIn ? 'Go to Dashboard' : 'Try it now and create an account'}
              </Button>
            </div>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6 text-primary-foreground/90">
              <div>
                <p className="text-3xl font-semibold">Multi-LLM</p>
                <p className="text-sm">Ensembles of GPT, Claude, and Gemini tuned for NSW history.</p>
              </div>
              <div>
                <p className="text-3xl font-semibold">Rubric-first</p>
                <p className="text-sm">Criteria are parsed and weighted before any scoring occurs.</p>
              </div>
              <div>
                <p className="text-3xl font-semibold">Minutes, not nights</p>
                <p className="text-sm">Upload one essay or a class set and get ready-to-share feedback.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature highlights */}
      <section className="max-w-6xl mx-auto px-6 py-16 space-y-10">
        <div className="max-w-3xl">
          <p className="text-sm font-semibold text-primary uppercase tracking-wider mb-3">Why teachers choose AI Marker Mate</p>
          <h2 className="text-3xl font-semibold mb-4">Purpose-built for Australian history assessments</h2>
          <p className="text-muted-foreground text-lg">
            This is not a chat box. It is a marking stack that mirrors the nuance of HSC decision making and surfaces professional-grade insights.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {featureHighlights.map((feature) => (
            <div key={feature.title} className="bg-card rounded-lg p-6 shadow-soft border border-border">
              <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
              <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Process */}
      <section className="bg-muted py-16 border-y border-border">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-3xl mb-10">
            <p className="text-sm font-semibold text-primary uppercase tracking-widest mb-3">Under the hood</p>
            <h2 className="text-3xl font-semibold mb-4">What happens after you press "Mark Essay"</h2>
            <p className="text-muted-foreground text-lg">
              Each stage adds context, calibration, or moderation that a vanilla LLM prompt simply cannot deliver on its own.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {processPillars.map((pillar) => (
              <div key={pillar.step} className="rounded-lg border border-border p-6 shadow-sm bg-card">
                <p className="text-sm font-semibold text-primary mb-2">{pillar.step}</p>
                <p className="text-muted-foreground">{pillar.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Differentiators */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="max-w-3xl mb-10">
          <p className="text-sm font-semibold text-primary uppercase tracking-wider mb-3">More than "copy and paste into GPT"</p>
          <h2 className="text-3xl font-semibold mb-4">Where raw LLM attempts fall short and how we fix it</h2>
          <p className="text-muted-foreground text-lg">
            Teachers told us they tried using ChatGPT or Gemini directly, only to receive flattering but inaccurate marks. AI Marker Mate adds the layers required for defensible, HSC-aligned judgements.
          </p>
        </div>
        {differentiators.map((diff) => (
          <div key={diff.markerMate} className="bg-card rounded-lg shadow-soft border border-border overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-[0.8fr_1.2fr] items-stretch min-h-[360px] md:min-h-[420px]">
              <div className="p-8 border-b md:border-b-0 md:border-r border-border bg-muted">
                <h3 className="text-xl font-semibold mb-3">{diff.llm}</h3>
                <p className="text-sm text-muted-foreground mb-5">Great for ideation, not for replicating professional marking.</p>
                <ul className="space-y-3">
                  {diff.items.map((item, idx) => (
                    <li key={`${diff.llm}-${idx}`} className="flex items-start gap-3 text-foreground text-sm leading-relaxed">
                      <span className="mt-1 h-2 w-2 rounded-full bg-primary flex-shrink-0" aria-hidden />
                      <span>{item.llm}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative bg-card flex items-center justify-center min-h-[320px]">
                <img
                  src={vennImage}
                  alt="Venn diagram contrasting accurate HSC marking with generic LLM marks"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* Trust & reassurance */}
      <section className="bg-foreground text-background py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-10 items-start">
            <div>
              <p className="text-sm font-semibold tracking-widest uppercase text-background/60 mb-3">Designed with teachers</p>
              <h2 className="text-3xl font-semibold mb-4">Built to support, not replace, professional judgement</h2>
              <p className="text-background/80 text-lg mb-6">
                Every insight is transparent and auditable. You can see the rationale, rubric alignment, and even the variance across models before you accept a mark.
              </p>
              <ul className="space-y-4 text-background/80">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-2 h-2 rounded-full bg-emerald-400 mt-2" />
                  Calibration reports benchmarked with senior HSC markers and annotated exemplars.
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-2 h-2 rounded-full bg-emerald-400 mt-2" />
                  Automatic variance alerts highlight any essay that deserves your closer review.
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-2 h-2 rounded-full bg-emerald-400 mt-2" />
                  Produces student-friendly feedback plus moderation-ready notes in the same run.
                </li>
              </ul>
            </div>
            <div className="bg-background/5 border border-background/10 rounded-lg p-8 space-y-6">
              <div>
                <h3 className="text-xl font-semibold mb-2">See it in your own context</h3>
                <p className="text-background/70">
                  Upload one assessment, attach the rubric you already use, and compare our mark with your own judgement to feel the difference immediately.
                </p>
              </div>
              <div className="bg-black/30 border border-background/10 rounded-lg p-6 space-y-4">
                <p className="text-sm uppercase tracking-widest text-emerald-300">Guiding principle</p>
                <p className="text-lg text-background">
                  Educators remain the final authority. AI Marker Mate accelerates the path to a confident, defensible mark, but it never removes your professional discretion.
                </p>
              </div>
              <Button onClick={onGetStarted} size="lg" className="w-full bg-background text-foreground hover:bg-background/90">
                {isLoggedIn ? 'Go to Dashboard' : 'Try it now and create an account'}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <svg className="w-8 h-8 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5.64 18h12.72L12 4 5.64 18z" />
            </svg>
            <div>
              <h3 className="text-lg font-semibold text-amber-900 dark:text-amber-200 mb-2">Professional judgement still matters</h3>
              <p className="text-amber-800 dark:text-amber-300">
                AI Marker Mate delivers highly accurate, HSC-aligned marks and feedback, but it is not intended to completely replace a teacher reading the
                response and applying their own expertise. Treat the result as a fast, deeply informed second marker that protects your time while elevating the
                quality of insights you share.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
